/**
 * /admin/parents — Admin parent CRUD landing (Fase 2 N-15).
 *
 * Thin wrapper around <ParentCrudPanel>. beforeLoad enforces admin RBAC and
 * redirects non-admins to their own home (mirror of /admin/students).
 */

import type { ReactNode } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { freshSessionQuery } from '../../lib/api/queries.js';
import { ParentCrudPanel, type StudentPickerOption } from '../../components/admin/ParentCrudPanel.js';

export const Route = createFileRoute('/admin/parents')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'tutor' && user.isApprovedTutor) {
      throw redirect({ to: '/dashboard' });
    }
    if (user.role === 'tutor' || user.kind === 'student') {
      throw redirect({ to: '/me' });
    }
    if (user.role !== 'admin') {
      throw redirect({ to: '/' });
    }
    return null;
  },
  component: AdminParentsPage,
});

function AdminParentsPage(): ReactNode {
  // fetchJson is a thin wrapper that drops cookies and parses JSON. We unwrap
  // the { ok, data } envelope so the Panel can access typed fields directly
  // (matches the behaviour of fetchApi for /api/auth/me etc.).
  const fetchJson = async <T,>(input: RequestInfo, init?: RequestInit): Promise<T> => {
    const res = await fetch(input, {
      ...init,
      credentials: 'include',
      headers: { ...(init?.headers ?? {}), accept: 'application/json' },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
      throw new Error(body.error?.message ?? `HTTP ${res.status}`);
    }
    const body = (await res.json()) as { ok?: boolean; data?: T };
    if (body.ok === false) {
      throw new Error('Operasi tidak berhasil.');
    }
    return body.data as T;
  };

  // BUG-ADM-02: wire the student picker to the existing admin students
  // search endpoint so step-2 "Pilih siswa" renders options (was empty).
  const studentOptionsResolver = async (search: string): Promise<StudentPickerOption[]> => {
    const params = new URLSearchParams({ search, limit: '25' });
    const data = await fetchJson<{
      students: { userId: string; name: string; email: string }[];
    }>(`/api/admin/students?${params.toString()}`);
    return (data.students ?? []).map((s) => ({ id: s.userId, name: s.name, email: s.email }));
  };

  return (
    <main className="page-shell">
      <PageHeader
        eyebrow="Manajemen"
        title="Manajemen orang tua"
        description="Buat akun orang tua, tautkan dengan siswa, dan kelola status aktif/nonaktif."
      />
      <ParentCrudPanel fetchJson={fetchJson} studentOptionsResolver={studentOptionsResolver} />
      <p className="footnote">
        Orang tua bisa onboard sendiri: kirim access link dari daftar di atas, lalu orang tua
        menautkan anaknya dan admin memverifikasi.
      </p>
    </main>
  );
}
